import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Container,
  Paper,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { AppColors } from '../theme/colors';
import { buildFunctionUrl } from '../utils/functionsUrl';
import {
  ADMIN_PASSWORD_RULE_ERROR,
  ADMIN_PASSWORD_RULE_LABEL,
  isValidAdminPassword,
} from '../utils/adminPassword';

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const token = searchParams.get('token');
  const companyId = searchParams.get('companyId');

  useEffect(() => {
    console.log('[ResetPasswordPage] URL パラメータ:', {
      token: token ? token.substring(0, 20) + '...' : null,
      companyId,
      allParams: Object.fromEntries(searchParams.entries())
    });
    
    if (!token) {
      setError('無効なパスワードリセットリンクです。トークンが見つかりません。');
    }
    
    if (!companyId) {
      setError('無効なパスワードリセットリンクです。会社IDが見つかりません。');
    }
  }, [token, companyId, searchParams]);

  const handleResetPassword = async () => {
    if (!token) {
      setError('無効なパスワードリセットリンクです。トークンが見つかりません。');
      return;
    }

    if (!companyId) {
      setError('無効なパスワードリセットリンクです。会社IDが見つかりません。');
      return;
    }

    // 会社登録と同じ条件。サーバーでも同じ条件で弾く
    if (!isValidAdminPassword(password)) {
      setError(ADMIN_PASSWORD_RULE_ERROR);
      return;
    }

    if (password !== confirmPassword) {
      setError('パスワードが一致しません。');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // カスタムトークンでパスワードをリセット（会社ごとに独立）
      console.log('[ResetPasswordPage] パスワードリセット開始:', { 
        token: token.substring(0, 10) + '...',
        companyId 
      });
      
      const response = await fetch(
        buildFunctionUrl('resetPasswordWithToken'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
            companyId: companyId,
            newPassword: password,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[ResetPasswordPage] パスワードリセット失敗:', errorData);
        
        if (errorData.error === '無効なトークンです') {
          setError('無効なパスワードリセットリンクです。再度リセットメールを送信してください。');
        } else if (errorData.error === 'このリンクは既に使用されています') {
          setError('このリンクは既に使用されています。再度リセットメールを送信してください。');
        } else if (errorData.error === 'リンクの有効期限が切れています') {
          setError('パスワードリセットリンクの有効期限が切れています。再度リセットメールを送信してください。');
        } else if (errorData.error === '会社IDが一致しません') {
          setError('無効なパスワードリセットリンクです。');
        } else {
          setError(errorData.error || 'パスワードリセットに失敗しました。');
        }
        return;
      }

      console.log('[ResetPasswordPage] パスワードリセット成功');
      setSuccess(true);
      setTimeout(() => {
        navigate('/admin-login');
      }, 2000);
    } catch (error: any) {
      console.error('[ResetPasswordPage] パスワードリセットエラー:', error);
      setError(error.message || 'パスワードリセットに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !companyId) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error">
            {error || '無効なパスワードリセットリンクです。'}
          </Alert>
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => navigate('/admin-login')}
          >
            ログインページに戻る
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="success">
            新しいパスワードを設定しました。ログインページに移動します...
          </Alert>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* 坂本様ご指摘⑥: 「リセット」だと消える・初期化されるように読めるため、
            入力した内容が設定されることが伝わる言葉にする */}
        {/* スマホでは h4 の大きさだと「新しいパスワードの設 / 定」と1文字だけ折り返すため、小さくする */}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          sx={{ mb: 3, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          新しいパスワードの設定
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleResetPassword(); }}>
          <TextField
            fullWidth
            label="新しいパスワード"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            helperText={ADMIN_PASSWORD_RULE_LABEL}
            FormHelperTextProps={{ sx: { mx: 0 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="パスワード（確認）"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : '新しいパスワードを設定する'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate('/admin-login')}
            disabled={loading}
          >
            キャンセル
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

